/** @jsxImportSource @emotion/react */
import React, { useRef } from 'react'
import isTest from '@signal/utils/env/isTest'
import isStorybook from '@signal/utils/env/isStorybook'
import renderIf from '@signal/utils/renderIf'
import { useContexts } from './hooks'
import {
  ContextLoadableComponentPropsType,
  GenericFetchContextType
} from './types'
import { AnyReactComponentType } from '@signal/utils/types'

/**
 * @interface ContextLoadableParamsType
 * this interface defines the parameters that need to be passed to contextLoadable
 */
type ContextLoadableParamsType<T> = {
  fetchContexts: Array<React.Context<GenericFetchContextType>>
  Component: React.ComponentType<T>
  Loader?: React.ComponentType<T & { children: React.ReactNode }>
  Skeleton?: React.ComponentType<T>
  Wrapper?: React.ComponentType<T>
  useRefInTest?: boolean
}

/**
 * this function generates a loadable for use with the context fetcher
 * @template T - the type defining the properties that can be accepted by the component
 * @param {Array<React.Context>} fetchContexts - the array of fetch contexts
 * @param {React.Component} Component - the component to render if all fetch contexts are loaded
 * @param {React.Component} Loader - the component to invoke context fetchers to load the fetch contexts if needed
 * @param {React.Component} Skeleton - the component to render if all fetch contexts are not loaded (optional)
 * @return {React.FC} - a component which can render the appropriate content depending on whether all fetch contexts
 *   have successfully loaded
 */
export default function contextLoadable<T>({
  fetchContexts,
  Component,
  Loader,
  Skeleton,
  Wrapper,
  useRefInTest
}: ContextLoadableParamsType<T>): React.ComponentType<T> {
  const SkeletonUse = Skeleton || Component
  return (props: T): JSX.Element => {
    const noLoader = (props as ContextLoadableComponentPropsType).noLoader
    // store this component in a reference to avoid unwanted unmounts.  If this loadable component's parent updates,
    // React will unmount the ComponentOrSkeleton child if we re-create this component each update rather than pull it
    // from a reference
    const componentOrSkeletonRef = useRef<AnyReactComponentType>()

    let ComponentOrSkeleton = componentOrSkeletonRef.current
    if (!ComponentOrSkeleton) {
      ComponentOrSkeleton = props => {
        const contexts = useContexts(fetchContexts)
        const children = renderIf(
          Boolean(
            contexts.every(
              context =>
                !(context as GenericFetchContextType).isFetching ||
                (context as GenericFetchContextType).sawError
            )
          ),
          () => <Component {...props} />,
          () => <SkeletonUse {...props} />
        )

        return Wrapper ? (
          <Wrapper {...props}>{children}</Wrapper>
        ) : (
          <React.Fragment>{children}</React.Fragment>
        )
      }

      if (!isTest() || useRefInTest) {
        componentOrSkeletonRef.current = ComponentOrSkeleton
      }
    }

    if (
      Loader &&
      !noLoader &&
      ((props as ContextLoadableComponentPropsType).useMockData ||
        (!isTest() && !isStorybook()))
    ) {
      return (
        <Loader {...props}>
          <ComponentOrSkeleton {...props} />
        </Loader>
      )
    } else {
      return <ComponentOrSkeleton {...props} />
    }
  }
}
